'use client'

import { useTranslation } from '@/hooks/useTypedTranslation'
import { Globe } from 'lucide-react'
import { siteConfig } from '@/config/site'
import { FooterAd } from './AdComponents'
import { Link } from '@tanstack/react-router'
import { useLocalizedPath } from '@/hooks/useLocalizedPath'

export function Footer() {
  const { t, i18n } = useTranslation()
  const { social, url: siteUrl } = siteConfig
  const { getLocalizedPath } = useLocalizedPath()
	  const techItems = [
	    {
	      key: 'grok' as const,
	      title: t.footer.tech.grok.title,
	      body: t.footer.tech.grok.body,
	    },
	    {
	      key: 'gemini' as const,
	      title: t.footer.tech.gemini.title,
	      body: t.footer.tech.gemini.body,
	    },
	    {
	      key: 'lovable' as const,
	      title: t.footer.tech.lovable.title,
	      body: t.footer.tech.lovable.body,
	    },
	  ]

  return (
    <footer className="relative py-20 bg-foreground text-background">
      {/* 页脚广告 */}
      <div className="container mx-auto px-6 sm:px-8 lg:px-12 mb-8">
        <FooterAd />
      </div>
      
      <div className="container mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-12 gap-12">
          {/* Logo and Description */}
          <div className="col-span-12 md:col-span-4">
            <div>
              <div className="font-bagel text-background text-3xl tracking-wider mb-4">
                borninsea
              </div>
              <p className="text-background/70 leading-relaxed mb-6">
                {t.footer.description}
              </p>
              {/* Social Media Icons */}
              <div className="flex items-center space-x-6">
                {/* X (Twitter) */}
                <a
                  href={social.twitter.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:scale-110 gentle-animation cursor-pointer"
                  aria-label="Twitter"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#E5E7EB" aria-hidden="true">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>

                {/* YouTube */}
                <a
                  href={social.youtube.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:scale-110 gentle-animation cursor-pointer"
                  aria-label="YouTube"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#FF0000" aria-hidden="true">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>

                {/* GitHub */}
                <a
                  href={social.github.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:scale-110 gentle-animation cursor-pointer"
                  aria-label="GitHub"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#E5E7EB" aria-hidden="true">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                  </svg>
                </a>

                {/* LinkedIn */}
                <a
                  href={social.linkedin.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:scale-110 gentle-animation cursor-pointer"
                  aria-label="LinkedIn"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#0077B5" aria-hidden="true">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>

	          {/* Tools / Technology Core Section */}
          <div className="col-span-12 md:col-span-8">
            <div>
              <h4 className="font-black text-2xl text-background mb-4">{t.footer.toolsTitle}</h4>

	              {/* Technology core description */}
	              <p className="text-background/70 text-base mb-6 leading-relaxed">
	                {t.footer.toolsDescription}
	              </p>
	              <div className="grid md:grid-cols-3 gap-6">
	                {techItems.map((item) => (
	                  <div key={item.key} className="space-y-1">
	                    <div className="text-sm font-semibold text-background">
	                      {item.title}
	                    </div>
	                    <p className="text-xs text-background/80 leading-relaxed">
	                      {item.body}
	                    </p>
	                  </div>
	                ))}
	              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar - Simplified */}
        <div className="border-t border-background/20 pt-8 mt-16">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-sm text-background/70">
              {t.footer.copyright}
            </div>

            {/* Legal Links */}
            <div className="flex items-center gap-4 text-sm">
              <Link
                to={getLocalizedPath('/privacy-policy')}
                className="text-background/70 hover:text-background transition-colors"
              >
                {i18n.language === 'zh' ? '隐私政策' : 'Privacy Policy'}
              </Link>
              <span className="text-background/30">|</span>
              <Link
                to={getLocalizedPath('/terms-of-service')}
                className="text-background/70 hover:text-background transition-colors"
              >
                {i18n.language === 'zh' ? '服务条款' : 'Terms of Service'}
              </Link>
            </div>

            {/* Language Selector */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-background/70">
                <Globe className="w-4 h-4" />
                <span>{t.footer.language}:</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => i18n.changeLanguage('zh')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                    i18n.language === 'zh'
                      ? 'bg-background text-foreground'
                      : 'text-background/70 hover:text-background hover:bg-background/10'
                  }`}
                >
                  中文
                </button>
                <button
                  onClick={() => i18n.changeLanguage('en')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                    i18n.language === 'en'
                      ? 'bg-background text-foreground'
                      : 'text-background/70 hover:text-background hover:bg-background/10'
                  }`}
                >
                  English
                </button>
              </div>
            </div>

            <div className="text-sm text-background/70">
              <a href={siteUrl} target="_blank" rel="noopener noreferrer" className="hover:text-background transition-colors">
                borninsea.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
